import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { PopoverComponent, PopoverPlacement } from '../popover/popover.component';

/**
 * Botão icon-only de header que abre um MENU DE AÇÕES no estilo/comportamento padrão
 * do JEMS3 (o mesmo do botão "Ações" da Lista de Submissões). Encapsula:
 *   - o BOTÃO: classe global `.chair-header-icon-button` (geometria, hover, borda
 *     accent no estado "menu aberto", dark-mode) + variante semântica;
 *   - o MENU: `.tpc-user-menu` (chrome global de menu) renderizado dentro de
 *     `<app-popover>` (popover custom do JEMS3, substituindo `<mat-menu>` que
 *     existia aqui antes — ver Fase 1 da migração mat-menu → app-popover);
 *   - o COMPORTAMENTO de "menu aberto" (borda accent) ligado ao state do popover +
 *     atributos de acessibilidade (aria-haspopup/expanded gerenciados pelo popover).
 *
 * Use para qualquer botão+menu de ação, p/ ficarem idênticos em formatação e
 * comportamento (hover incluído). Os ITENS entram por PROJEÇÃO DE CONTEÚDO, no padrão:
 *
 *   <app-actions-menu-button [label]="'forms.buttons.actions' | translate">
 *     <a class="container-option">
 *       <button mat-menu-item (click)="...">
 *         <mat-icon fontSet="ph" fontIcon="ph-..."></mat-icon>
 *         <span>{{ 'chave' | translate }}</span>
 *       </button>
 *     </a>
 *     <mat-divider></mat-divider>
 *     ...
 *   </app-actions-menu-button>
 *
 * IMPORTANTE: a markup do conteúdo projetado segue idêntica (`mat-menu-item`,
 * `mat-divider`) — o CSS global de `.tpc-user-menu .container-option .mat-menu-item`
 * continua aplicando estilo. A diretiva `mat-menu-item` por si só funciona sem
 * `<mat-menu>` pai (só não participa da nav-teclado nativa, que não usamos aqui).
 */
@Component({
  selector: 'app-actions-menu-button',
  templateUrl: './actions-menu-button.component.html',
})
export class ActionsMenuButtonComponent {
  /** Material Symbol (ligatura) do gatilho. Default: kebab de "mais ações". */
  @Input() icon = 'more_vert';
  /** Texto JÁ TRADUZIDO usado no tooltip e no aria-label. */
  @Input() label = '';
  /** Modificador semântico da classe do botão (`chair-header-icon-button--<variant>`). */
  @Input() variant: 'actions' | 'filter' | 'download' = 'actions';
  /**
   * Posição horizontal do menu em relação ao botão (compatível com a API antiga
   * do mat-menu `xPosition`):
   *   - `before` → alinha a borda DIREITA do menu à direita do botão (default).
   *   - `after`  → alinha a borda ESQUERDA do menu à esquerda do botão.
   */
  @Input() xPosition: 'before' | 'after' = 'before';
  /** Desabilita o gatilho: cinza, sem hover e sem abrir o menu. */
  @Input() disabled = false;
  /** Oculta o tooltip do gatilho (mantém o `aria-label`). Default `false`. */
  @Input() disableTooltip = false;

  /** Tradução interna de `xPosition` (API mat-menu) para `placement` (API popover). */
  get popoverPlacement(): PopoverPlacement {
    return this.xPosition === 'before' ? 'bottom-end' : 'bottom-start';
  }

  // O `<button mat-icon-button #trigger>` é um COMPONENTE Material (MatButton),
  // não uma directive — então `#trigger` no template resolve pra INSTÂNCIA do
  // MatButton (sem `getBoundingClientRect`), o que faz o `app-popover.position()`
  // explodir. `read: ElementRef` força o ViewChild a pegar o HTMLElement DOM.
  @ViewChild('trigger', { read: ElementRef, static: true })
  private triggerRef!: ElementRef<HTMLElement>;
  /** HTMLElement do botão-gatilho — exposto pro `[anchor]` do popover no template. */
  get triggerEl(): HTMLElement { return this.triggerRef?.nativeElement; }

  @ViewChild('pop') private popRef!: PopoverComponent;

  /**
   * Clique num ITEM projetado fecha o menu — semântica do mat-menu original,
   * que o `app-popover` não tem (ele só fecha em clique-fora/Esc; sem isto o
   * menu ficava aberto após a ação e "roubava" o clique seguinte). Só fecha
   * quando o alvo é um `.mat-menu-item` habilitado — cliques em divisor ou
   * conteúdo não-item mantêm o menu aberto.
   */
  onContentClick(ev: MouseEvent): void {
    const target = ev.target as HTMLElement;
    const item = target && target.closest ? target.closest('.mat-menu-item') : null;
    if (item && !item.hasAttribute('disabled') && this.popRef) {
      this.popRef.hide();
    }
  }
}
